import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Button from '@material-ui/core/Button';
import { t } from 'src/lib/language';
import { closeModal, openModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import { MODALS_SIZE } from 'src/models/modal';
import {
  MouseEvent, useCallback, useEffect, useState,
} from 'react';
import {
  InstallmentModel,
  PAYMENT_ACTION_TYPE,
  PAYMENT_FREQUENCY,
  PAYMENT_FREQUENCY_LIST,
  PAYMENT_STATUS,
  PAYMENT_TYPE,
} from 'src/models/PaymentModel';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { setError } from 'src/redux/actions/errorsActions';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import AdminDonationValidator from 'src/validations/AdminDonationValidator';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import {
  FormControl, FormControlLabel, Radio, RadioGroup, Tooltip,
} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import { postPurchaseAction } from 'src/redux/actions/paymentActions';
import ProductsSelector from 'src/components/Control/ProductsSelector';
import { capitalize, keyBy } from 'lodash';
import { INVOICE_FIELDS } from 'src/constants/Invoices';
import ContactSelector from 'src/components/Control/ContactSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import PaymentAccountSelector from 'src/components/Control/PaymentAccountSelector';
import OrganisationBankAccountSelector
  from 'src/components/Control/OrganisationBankAccountSelector';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import TextLink from 'src/components/UI/TextLink';
import Subtitle from 'src/components/UI/Subtitle';
import MetadataSelector, { MetadataType } from 'src/components/Control/MetadataSelector';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { getOrganisationBankAccountAction } from 'src/redux/actions/paymentInfoAction';
import { getGatewayListAction } from 'src/redux/actions/gatewayAction';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import { ProcessingType } from 'src/models/GatewayModel';
import { ProductList, ProductType } from 'src/models/ProductModel';
import { ProductOrderModel, TicketInfo } from 'src/models/InvoiceModel';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { fromOrgTimezone } from 'src/lib/DateHelper';
import FormSingleSelect from 'src/components/Control/FormControls/FormSingleSelect';
import DonationModel from 'src/models/DonationModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { getContactAction } from 'src/redux/actions/contactActions';
import { ContactPayload } from 'src/models/ContactModel';
import ManageAccountsButton from 'src/components/Control/Buttons/ManageAccountsButton';
import ManageDepartmentsButton
  from 'src/components/Control/Buttons/ManageDepartmentsButton';
import ManageProgramsButton from 'src/components/Control/Buttons/ManageProgramsButton';
import ManageProductsButton from 'src/components/Control/Buttons/ManageProductsButton';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import Title from 'src/components/UI/Title';
import Scroller from 'src/components/UI/Scroller';
import Form from 'src/components/UI/Form';
import Col2 from 'src/components/UI/Col2';
import Col3 from 'src/components/UI/Col3';
import FundraisingSelector from 'src/components/Control/FundraisingSelector';
import AmbassadorSelector from 'src/components/Control/AmbassadorSelector';
import Col4 from 'src/components/UI/Col4';
import TicketInformationFields from 'src/components/Elements/TicketInformationFields';
import { FundraisingLink } from 'src/models/FundraisingModel';
import { FundraisingLinkValidator } from 'src/validations/FundraisingTransactionValidator';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';

const MakeDonationForm = () => {
  const dispatch = useAppDispatch();
  const products = useSelector((state: Store) => state.products || [] as ProductList);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const gateways = useSelector((state: Store) => state.gateways);
  const currentContactId = useSelector(
    (state: Store) => state.currentEntity[ENTITIES.contacts] || 0,
  );
  const processing = useSelector(
    (state: Store) => state.formStatus[FORM.post_purchase] === FORM_STATUS.processing,
  );
  const timezone = useSelector(
    (state: Store) => state.data.timezones[state.organisation.timezoneId],
  );
  const [contactId, setContactId] = useState(currentContactId);
  const [gatewayCcBankAccountId, setGatewayCcBankAccountId] = useState(0);
  const [gatewayEftBankAccountId, setGatewayEftBankAccountId] = useState(0);
  const { contact: contactInfo } = useSelector(
    (state: Store) => {
      if (!!state.contacts[contactId]) {
        return state.contacts[contactId] as ContactPayload;
      }
      return {} as ContactPayload;
    },
  );

  const getName = () => {
    if (!contactInfo) return '';
    if (contactInfo.company_name) {
      return contactInfo.company_name;
    }
    return `${contactInfo.first_name} ${contactInfo.last_name}`;
  };

  const [orderDonationState, setOrderDonationState] = useState({} as ProductOrderModel);

  const [installmentState, setInstallmentState] = useState({
    number: 1,
    start_date: moment().startOf('day'),
    frequency: PAYMENT_FREQUENCY.monthly,
  } as InstallmentModel);

  const [donationState, setDonationState] = useState({
    is_installment: 0,
    payment_type: PAYMENT_TYPE.cc,
    payment_action: PAYMENT_ACTION_TYPE.credit,
    payment_status: PAYMENT_STATUS.scheduled,
    paid_date: moment().startOf('day'),
    scheduled_date: moment().startOf('day'),
    is_promised_donation: false,
    donor_name_displayed: getName(),
  } as DonationModel);

  const [fundraisingState, setFundraisingState] = useState({} as FundraisingLink);

  const [ticketsState, setTicketsState] = useState([] as TicketInfo[]);

  const isOnlinePayment =
    () => [PAYMENT_TYPE.cc, PAYMENT_TYPE.eft].includes(donationState.payment_type);

  const isOfflinePayment =
    () => ![PAYMENT_TYPE.cc, PAYMENT_TYPE.eft].includes(donationState.payment_type);

  const getProduct = (id:number) => (
    products && products.find((product) => product.id === id)
  );

  useEffect(() => {
    if (orderDonationState.product_id) {
      const product = getProduct(orderDonationState.product_id ?? 0);
      if (product) {
        setOrderDonationState({
          ...orderDonationState,
          quantity: 1,
          paid: product?.price ?? 0,
          deductible: (product?.deductible ?? 0) * 100,
          discount: product?.discount ?? 0,
        });
        setDonationState({
          ...donationState,
          bank_account_id: product.bank_account_id,
          department_id: product.department_id,
          account_id: product.account_id,
          program_id: product.program_id,
          is_deductible: !!product.deductible,
          payment_gateway_id: product.payment_gateway_cc_id,
          title: product[`name_${lang}`],
        });
      }
    }
    // Reset tickets when product changes
    setTicketsState([]);
  }, [orderDonationState.product_id]);

  // Reset product and tickets when fundraising changes
  useEffect(() => {
    setOrderDonationState((prev) => ({ ...prev, product_id: 0, quantity: 0 }));
    setTicketsState([]);
  }, [fundraisingState.event_id]);

  useEffect(() => {
    if (contactInfo) {
      setDonationState({
        ...donationState,
        donor_name_displayed: getName(),
        issue_tax_receipt: Boolean(contactInfo.issue_tax_receipt),
      });
    }
  }, [contactInfo]);

  // Sync tickets array with quantity
  useEffect(() => {
    const quantity = orderDonationState.quantity || 1;
    setTicketsState((prev) => {
      if (prev.length === quantity) return prev;
      if (prev.length > quantity) return prev.slice(0, quantity);
      const added = Array.from(
        { length: quantity - prev.length },
        () => ({} as TicketInfo),
      );
      return [...prev, ...added];
    });
  }, [orderDonationState.quantity]);

  useEffect(() => {
    dispatch(getOrganisationBankAccountAction());
    dispatch(setError({ [FORM.make_payment]: {} }));
    dispatch(getGatewayListAction());
  }, []);

  const renderInstallmentDetails = () => {
    const total = (orderDonationState.quantity ?? 0) * (
      (orderDonationState.paid ?? 0) - (orderDonationState.discount ?? 0)
    );
    const step = total / (installmentState.number ?? 1);

    if (!Number.isNaN(step) && !!installmentState.number) {
      return (
        <span style={{ fontStyle: 'italic' }}>
          {installmentState.number} {capitalize(installmentState.frequency)}&nbsp;
          {t(lang, 'forms.payments.payments_of')}&nbsp;
          {step.toFixed(2)}$
        </span>
      );
    }
    return <></>;
  };

  const getOnlinePaymentStatus = () => (
    [
      {
        text: PAYMENT_STATUS.scheduled,
        id: PAYMENT_STATUS.scheduled,
      },
    ]
  );

  const getOfflinePaymentStatus = () => (
    [
      {
        text: PAYMENT_STATUS.paid,
        id: PAYMENT_STATUS.paid,
      },
      {
        text: PAYMENT_STATUS.scheduled,
        id: PAYMENT_STATUS.scheduled,
      },
    ]
  );

  const renderInstallmentFrequencyOptions = () => (
    PAYMENT_FREQUENCY_LIST.map((frequency:PAYMENT_FREQUENCY, index:number) => (
      <MenuItem value={frequency} key={`payment-frequency-${index}`}>{frequency}</MenuItem>
    ))
  );

  const openAddCreditCardModal = () => {
    dispatch(openModal({
      modal: MODALS.addCreditCard,
      payload: {
        contact_id: donationState.contact_id,
      },
    }));
  };

  const openAddBankAccountModal = () => {
    dispatch(openModal({
      modal: MODALS.addBankAccount,
      payload: {
        contact_id: donationState.contact_id,
      },
    }));
  };

  const openEditBankAccountModal = () => {
    dispatch(openModal({
      modal: MODALS.editBankAccount,
      payload: {
        contactId: donationState.contact_id,
        accountId: donationState.payment_info_id,
      },
    }));
  };

  const onOrderChange = (value: Value, field: string) => {
    setOrderDonationState({
      ...orderDonationState,
      [field]: value,
    });
  };

  const onFieldChange = (value: Value, field: string) => {
    if (field === 'is_installment') {
      if (!!value) {
        setDonationState({
          ...donationState,
          is_installment: 1,
          start_date: donationState.scheduled_date || moment().startOf('day'),
        });
        setInstallmentState({
          ...installmentState,
          start_date: donationState.scheduled_date || moment().startOf('day'),
        });
        return;
      }
    }
    setDonationState({
      ...donationState,
      [field]: value,
    });
  };

  const onFundraisingFieldChange = useCallback((value: Value, field: string) => {
    setFundraisingState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onPaymentTypeChange = (value: PAYMENT_TYPE) => {
    if (isOnlinePayment()) {
      setDonationState({
        ...donationState,
        bank_account_id: (value === PAYMENT_TYPE.cc)
          ? gatewayCcBankAccountId
          : gatewayEftBankAccountId,
        payment_type: value,
        payment_status: PAYMENT_STATUS.scheduled,
      });
      return;
    }

    setDonationState({
      ...donationState,
      payment_type: value,
    });
  };

  const onGatewayChange = (value: number) => {
    const selectedGateway = keyBy(gateways, 'id')[value];
    if (selectedGateway.bank_account_id) {
      if (donationState.payment_type === PAYMENT_TYPE.cc) {
        setGatewayCcBankAccountId(selectedGateway.bank_account_id);
      } else {
        setGatewayEftBankAccountId(selectedGateway.bank_account_id);
      }

      setDonationState({
        ...donationState,
        payment_gateway_id: value,
        bank_account_id: selectedGateway.bank_account_id,
      });
    }
  };

  const onContactChange = (id:number) => {
    onFieldChange(id, 'contact_id');
    setContactId(id);
    dispatch(getContactAction(id, FORM.admin_donation));
  };

  const onInstallmentChange = (value: Value, field: string) => {
    setInstallmentState({
      ...installmentState,
      [field]: value,
    });
  };

  const getDates = () => {
    const base = {
      issued_date: fromOrgTimezone(donationState.issued_date, timezone),
      due_date: fromOrgTimezone(donationState.due_date, timezone),
    };
    if (donationState.is_installment) {
      return {
        ...base,
        paid_date: undefined,
        scheduled_date: undefined,
        start_date: fromOrgTimezone(installmentState.start_date, timezone),
      };
    }
    if (donationState.payment_status === PAYMENT_STATUS.paid) {
      return {
        ...base,
        paid_date: fromOrgTimezone(donationState.paid_date, timezone),
        scheduled_date: undefined,
        start_date: undefined,
      };
    }
    return {
      ...base,
      paid_date: undefined,
      scheduled_date: fromOrgTimezone(donationState.scheduled_date, timezone),
      start_date: undefined,
    };
  };

  const getOrder = () => ([{
    ...orderDonationState,
    deductible: (orderDonationState.deductible ?? 0) / 100,
    tickets: ticketsState,
  }]);

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.adminDonation }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.admin_donation]: {} }));
    const data = {
      ...donationState,
      ...orderDonationState,
      tickets: ticketsState,
    };
    if (isOfflinePayment()) {
      data.payment_gateway_id = undefined;
    }
    try {
      if (fundraisingState.is_linked) {
        FundraisingLinkValidator()
          .validateSync(fundraisingState, { abortEarly: false });
      }
      const selectedProduct = getProduct(orderDonationState.product_id ?? 0);
      const ticketFields: string[] = [];
      if (selectedProduct) {
        if (selectedProduct.first_name_required) ticketFields.push('first_name');
        if (selectedProduct.last_name_required) ticketFields.push('last_name');
        if (selectedProduct.email_required) ticketFields.push('email');
        if (selectedProduct.phone_required) ticketFields.push('phone');
        if (selectedProduct.identifier_required) ticketFields.push('identifiers');
      }
      AdminDonationValidator(lang, ticketFields).validateSync(data, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: t(lang, 'forms.contact.validation_errors'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.admin_donation]: errorBag }));
      return;
    }

    if (donationState.account_id < 0) return;
    // INSTALLMENT
    if (donationState.is_installment) {
      const payload = {
        ...donationState,
        paid_date: getDates().paid_date,
        scheduled_date: getDates().scheduled_date,
        start_date: getDates().start_date,
        issued_date: getDates().issued_date,
        due_date: getDates().due_date,
        payment_info_id: isOnlinePayment() ? donationState.payment_info_id : undefined,
        orders: getOrder(),
        installment: installmentState,
        event: fundraisingState.is_linked ? fundraisingState : undefined,
      };
      dispatch<any>(postPurchaseAction(payload));
      return;
    }

    // ONLINE
    if (isOnlinePayment()) {
      const payload = {
        ...donationState,
        paid_date: getDates().paid_date,
        scheduled_date: getDates().scheduled_date,
        start_date: getDates().start_date,
        issued_date: getDates().issued_date,
        due_date: getDates().due_date,
        installment: undefined,
        orders: getOrder(),
        event: fundraisingState.is_linked ? fundraisingState : undefined,
      };
      dispatch<any>(postPurchaseAction(payload));
      return;
    }

    // OFFLINE
    const payload = {
      ...donationState,
      payment_info_id: undefined,
      paid_date: getDates().paid_date,
      scheduled_date: getDates().scheduled_date,
      start_date: getDates().start_date,
      issued_date: getDates().issued_date,
      due_date: getDates().due_date,
      installment: undefined,
      orders: getOrder(),
      event: fundraisingState.is_linked ? fundraisingState : undefined,
    };
    dispatch(postPurchaseAction(payload));
  };

  const DisplayPaymentStatusSelector = () => {
    if (isOfflinePayment()) {
      return (
        <FormSingleSelect
          form={FORM.admin_donation}
          name="paymentStatus"
          label={t(lang, 'forms.invoices.payment_status')}
          onChange={(status: string) => onFieldChange(status, 'payment_status')}
          value={donationState.payment_status}
          items={getOfflinePaymentStatus()}
          disabled={processing}
          noMarginTop />
      );
    }
    return (
      <FormSingleSelect
        form={FORM.admin_donation}
        name="paymentStatus"
        label={t(lang, 'forms.invoices.payment_status')}
        onChange={(status: string) => onFieldChange(status, 'payment_status')}
        value={donationState.payment_status || PAYMENT_STATUS.scheduled}
        items={getOnlinePaymentStatus()}
        disabled
        noMarginTop />
    );
  };

  const DisplayGatewaySelector = () => {
    switch (donationState.payment_type) {
      case PAYMENT_TYPE.cc:
        return (
          <GatewaySelector
            form={FORM.admin_donation}
            name="payment_gateway_id"
            onChange={onGatewayChange}
            type={ProcessingType.cc}
            value={donationState.payment_gateway_id}
            noMarginTop />
        );
      case PAYMENT_TYPE.eft:
        return (
          <GatewaySelector
            form={FORM.admin_donation}
            name="payment_gateway_id"
            onChange={onGatewayChange}
            type={ProcessingType.eft}
            value={donationState.payment_gateway_id}
            noMarginTop />
        );
      default:
        return (<></>);
    }
  };

  const DisplayPaymentInfoSelector = () => {
    if (donationState.payment_type === PAYMENT_TYPE.cc) {
      return (
        <TextLink onClick={() => openAddCreditCardModal()}>{t(lang, 'forms.payments.add_credit_card')}</TextLink>
      );
    }
    if (donationState.payment_type === PAYMENT_TYPE.eft) {
      if (donationState.payment_info_id && donationState.contact_id) {
        return (
          <>
            <TextLink onClick={() => openAddBankAccountModal()}>{t(lang, 'forms.payments.add_bank_account')}</TextLink>
            <TextLink onClick={() => openEditBankAccountModal()}>{t(lang, 'forms.payments.edit_bank_account')}</TextLink>
          </>
        );
      }
      return (
        <TextLink onClick={() => openAddBankAccountModal()}>{t(lang, 'forms.payments.add_bank_account')}</TextLink>
      );
    }
    return (<></>);
  };

  const DisplayDate = () => {
    if (donationState.is_installment) {
      return (
        <FormDatePicker
          form={FORM.admin_donation}
          required
          label={t(lang, 'forms.payments.start_date')}
          name="start_date"
          onChange={
            (value: MaterialUiPickersDate) => onInstallmentChange(
              value,
              'start_date',
            )
          }
          value={moment(installmentState.start_date) || moment()} />
      );
    }
    if (isOnlinePayment()) {
      return (
        <FormDatePicker
          form={FORM.admin_donation}
          required
          label={t(lang, 'forms.payments.paid_date')}
          name="scheduled_date"
          onChange={
            (value: MaterialUiPickersDate) => onFieldChange(
              value,
              'scheduled_date',
            )
          }
          value={moment(donationState.scheduled_date)}
          disabled={processing} />
      );
    }
    if (isOfflinePayment()) {
      return (
        <FormDatePicker
          form={FORM.admin_donation}
          required
          label={t(lang, 'forms.payments.paid_date')}
          name="paid_date"
          onChange={
            (value: MaterialUiPickersDate) => onFieldChange(
              value,
              'paid_date',
            )
          }
          value={moment(donationState.paid_date)}
          disabled={processing} />
      );
    }

    return <></>;
  };

  const DisplayTotalAmount = () => ((orderDonationState.quantity ?? 0) * (
    orderDonationState.paid - (orderDonationState.discount ?? 0)
  )).toFixed(2);

  const PaymentTypesLabels = () => {
    const PaymentTypeStyle = {
      margin: '0px',
      marginLeft: '-8px',
      marginTop: '-8px',
    };
    return (
      <>
        <Row>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.cc}
              control={<Radio />}
              label={PAYMENT_TYPE.cc}
              key="payment-type-7" />
          </Col2>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.eft}
              control={<Radio />}
              label={PAYMENT_TYPE.eft}
              key="payment-type-8" />
          </Col2>
        </Row>
        <Row>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.cash}
              control={<Radio />}
              label={PAYMENT_TYPE.cash}
              key="payment-type-0" />
          </Col2>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.check}
              control={<Radio />}
              label={PAYMENT_TYPE.check}
              key="payment-type-1" />
          </Col2>
        </Row>
        <Row>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.inkind}
              control={<Radio />}
              label={PAYMENT_TYPE.inkind}
              key="payment-type-2" />
          </Col2>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.interac}
              control={<Radio />}
              label={PAYMENT_TYPE.interac}
              key="payment-type-3" />
          </Col2>
        </Row>
        <Row>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.terminal}
              control={<Radio />}
              label={PAYMENT_TYPE.terminal}
              key="payment-type-4" />
          </Col2>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.userbalance}
              control={<Radio />}
              label={PAYMENT_TYPE.userbalance}
              key="payment-type-5" />
          </Col2>
        </Row>
        <Row>
          <Col2>
            <FormControlLabel
              disabled={processing}
              style={PaymentTypeStyle}
              value={PAYMENT_TYPE.wired}
              control={<Radio />}
              label={PAYMENT_TYPE.wired}
              key="payment-type-6" />
          </Col2>
        </Row>
      </>
    );
  };

  const Closebutton = () => (
    <Button
      style={{ marginLeft: 'auto' }}
      variant="contained"
      color="primary"
      size="small"
      onClick={handleClose}
      disabled={processing}>
      Close
    </Button>
  );

  const PayButton = () => (
    <Tooltip
      title={t(lang, 'forms.payments.make_donation')}
      placement="top-start">
      <span>
        <ActionButton
          category={MENU_BUTTON_CATEGORY.action}
          onClick={handleSubmit}
          disabled={processing}
          loading={processing}>
          <AttachMoneyIcon />
        </ActionButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      <Header height="86px">
        <Row height="auto">
          <Title>{t(lang, 'forms.payments.make_donation')}</Title>
          <Closebutton />
        </Row>
        <Row height="auto">
          <PayButton />
          <ManageAccountsButton disabled={processing} />
          <ManageDepartmentsButton disabled={processing} />
          <ManageProgramsButton disabled={processing} />
          <ManageProductsButton disabled={processing} />
        </Row>
      </Header>
      <Scroller height="608px">
        <Form maxWidth={MODALS_SIZE.large}>
          <Row height="auto">
            <FormTextField
              form={FORM.admin_donation}
              label={t(lang, 'forms.invoices.title')}
              name="title"
              onChange={onFieldChange}
              value={donationState.title}
              disabled={processing} />
          </Row>
          {/* Contact */}
          <Row height="auto">
            <Col2>
              <ContactSelector
                style={{ minWidth: '100%' }}
                autocompleteId={AUTOCOMPLETE_ID.add_payment_contact}
                onChange={(id:number) => onContactChange(id)}
                label={t(lang, 'forms.payments.contact')}
                form={FORM.admin_donation}
                name="contact_id"
                contactId={donationState.contact_id}
                disabled={processing}
                hideEdit={!!!donationState.contact_id} />
            </Col2>
            <Col2>
              <ContactSelector
                style={{ minWidth: '100%' }}
                autocompleteId={AUTOCOMPLETE_ID.add_payment_contact}
                onChange={(id:number) => onFieldChange(id, INVOICE_FIELDS.seller_id)}
                label={t(lang, 'forms.payments.seller')}
                form={FORM.admin_donation}
                name={INVOICE_FIELDS.seller_id}
                contactId={donationState.seller_id}
                disabled={processing}
                hideEdit={!!!donationState.seller_id} />
            </Col2>
          </Row>
          {/* Fundraising */}
          <Row height="auto">
            <FormCheckbox
              label="Link this donation to a fundraising"
              name="is_linked"
              onChange={onFundraisingFieldChange}
              checked={!!fundraisingState.is_linked}
              disabled={processing} />
          </Row>
          { fundraisingState.is_linked && (
          <>
            <Row height="auto">
              <FundraisingSelector
                form={FORM.admin_donation}
                label="Fundraising"
                name="event_id"
                value={fundraisingState.event_id}
                onChange={(id) => onFundraisingFieldChange(id, 'event_id')} />
              <AmbassadorSelector
                form={FORM.admin_donation}
                fundraisingId={fundraisingState.event_id}
                label="Ambassador"
                value={fundraisingState.ambassador_id}
                name="ambassador_id"
                onChange={(id) => onFundraisingFieldChange(id, 'ambassador_id')} />
            </Row>
            <Row>
              <Col4>
                <FormTextField
                  form={FORM.admin_donation}
                  label="Amount multiplier"
                  name="amount_multiplier"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.amount_multiplier} />
              </Col4>
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.admin_donation}
                  label="Donor name displayed"
                  name="donor_name_displayed"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.donor_name_displayed} />
              </Col2>
              <Col2>
                <FormCheckbox
                  label="Anonymous Donor"
                  name="is_anonymous"
                  onChange={onFundraisingFieldChange}
                  checked={!!fundraisingState.is_anonymous}
                  disabled={processing} />
              </Col2>
            </Row>
            <Row height="auto">
              <FormTextField
                form={FORM.admin_donation}
                label="Donor Comment"
                name="donor_comment"
                onChange={onFundraisingFieldChange}
                multiline
                minRows={3}
                maxRows={5}
                value={fundraisingState.donor_comment} />
            </Row>
            <Row height="auto">
              <Col2>
                <FormTextField
                  form={FORM.admin_donation}
                  label="Source reference"
                  name="source_reference"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference} />
              </Col2>
              <Col2>
                <FormTextField
                  form={FORM.admin_donation}
                  label="Source reference URL"
                  name="source_reference_url"
                  onChange={onFundraisingFieldChange}
                  value={fundraisingState.source_reference_url} />
              </Col2>
            </Row>
          </>
          )}
          {/* Product */}
          <Row height="auto">
            <ProductsSelector
              id="product-selector"
              form={FORM.admin_donation}
              name="product_id"
              onChange={(id) => onOrderChange(id, 'product_id')}
              value={orderDonationState.product_id ?? 0}
              excludeType={!fundraisingState.event_id ? ProductType.ticket : undefined}
              disabled={processing} />
          </Row>
          { !donationState.is_deductible && (
          <Row height="auto" style={{ alignItems: 'flex-start' }}>
            <Col3>
              <FormTextField
                form={FORM.admin_donation}
                label={t(lang, 'forms.invoices.amount')}
                name="paid"
                onChange={onOrderChange}
                value={orderDonationState.paid}
                disabled={processing} />
            </Col3>
            <Col3>
              <FormTextField
                form={FORM.admin_donation}
                label={t(lang, 'forms.invoices.quantity')}
                name="quantity"
                onChange={onOrderChange}
                value={orderDonationState.quantity}
                disabled={processing} />
            </Col3>
            <Col3>
              <FormTextField
                form={FORM.admin_donation}
                label={t(lang, 'forms.invoices.discount')}
                name="discount"
                onChange={onOrderChange}
                value={orderDonationState.discount}
                disabled={processing} />
            </Col3>
          </Row>
          )}
          { donationState.is_deductible && (
            <Row height="auto">
              <Col3>
                <FormTextField
                  form={FORM.admin_donation}
                  label={t(lang, 'forms.invoices.amount')}
                  name="paid"
                  onChange={onOrderChange}
                  value={orderDonationState.paid}
                  disabled={processing} />
              </Col3>
              <div style={{ width: '20%' }}>
                <FormTextField
                  form={FORM.admin_donation}
                  label={t(lang, 'forms.invoices.quantity')}
                  name="quantity"
                  onChange={onOrderChange}
                  value={orderDonationState.quantity}
                  disabled={processing} />
              </div>
              <Col3>
                <FormTextField
                  form={FORM.admin_donation}
                  label={`${t(lang, 'forms.invoices.deductible')} (%)`}
                  name="deductible"
                  onChange={onOrderChange}
                  value={orderDonationState.deductible}
                  disabled={processing} />
              </Col3>
              <Col3>
                <FormTextField
                  form={FORM.admin_donation}
                  label={t(lang, 'forms.invoices.discount')}
                  name="discount"
                  onChange={onOrderChange}
                  value={orderDonationState.discount}
                  disabled={processing} />
              </Col3>
            </Row>
          )}
          {!!orderDonationState.product_id && (
            <strong>
              Total amount: {DisplayTotalAmount()} CA$
            </strong>
          )}
          {!!orderDonationState.product_id && (() => {
            const selectedProduct = getProduct(orderDonationState.product_id ?? 0);
            if (selectedProduct) {
              return (
                <TicketInformationFields
                  form={FORM.admin_donation}
                  product={selectedProduct}
                  tickets={ticketsState}
                  onChange={setTicketsState}
                  eventId={fundraisingState.event_id}
                  disabled={processing} />
              );
            }
            return null;
          })()}
          <Row height="auto">
            <FormCheckbox
              label="It's a promised donation"
              name="is_promised_donation"
              onChange={onFieldChange}
              checked={Boolean(donationState.is_promised_donation)}
              disabled={processing} />
          </Row>
          { donationState.is_promised_donation && (
          <>
            <Row height="auto">
              <Col2>
                <FormDatePicker
                  form={FORM.admin_donation}
                  label={t(lang, 'fields.invoices.issued_date')}
                  name="issued_date"
                  onChange={
                    (value: MaterialUiPickersDate) => onFieldChange(
                      value, 'issued_date',
                    )
                  }
                  value={moment(donationState.issued_date) || moment()}
                  disabled={processing} />
              </Col2>
              <Col2>
                <FormDatePicker
                  form={FORM.admin_donation}
                  label={t(lang, 'fields.invoices.due_date')}
                  name="due_date"
                  onChange={
                    (value: MaterialUiPickersDate) => onFieldChange(
                      value, 'due_date',
                    )
                  }
                  value={moment(donationState.due_date) || moment()}
                  disabled={processing} />
              </Col2>
            </Row>
          </>
          )}
          { !donationState.is_promised_donation && (
          <>
            <Subtitle noMargin>Payment</Subtitle>
            <Row height="auto" alignItems="end">
              <Col2>
                <span style={{ width: '50%' }}>
                  <FormDatePicker
                    form={FORM.admin_donation}
                    label={t(lang, 'fields.invoices.issued_date')}
                    name="issued_date"
                    onChange={
                  (value: MaterialUiPickersDate) => onFieldChange(
                    value, 'issued_date',
                  )
                }
                    value={moment(donationState.issued_date) || moment()}
                    disabled={processing} />
                </span>
              </Col2>
              <Col2>
                <FormCheckbox
                  label={t(lang, 'forms.payments.issue_tax_receipt')}
                  name="issue_tax_receipt"
                  onChange={onFieldChange}
                  checked={Boolean(donationState.issue_tax_receipt)}
                  disabled={processing} />
              </Col2>
            </Row>
            <Row height="auto">
              <Col2>
                <FormDatePicker
                  form={FORM.admin_donation}
                  label={t(lang, 'fields.invoices.due_date')}
                  name="due_date"
                  onChange={
                    (value: MaterialUiPickersDate) => onFieldChange(
                      value, 'due_date',
                    )
                  }
                  value={moment(donationState.due_date) || moment()}
                  disabled={processing} />
              </Col2>
            </Row>
            <Subtitle style={{ marginBottom: '16px' }}>Payment type</Subtitle>
            <Row height="auto">
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label={t(lang, 'forms.payments.payment_type')}
                  name="payment_type"
                  value={donationState.payment_type}
                  onChange={(e) => onPaymentTypeChange(e.target.value as PAYMENT_TYPE)}>
                  <PaymentTypesLabels />
                </RadioGroup>
              </FormControl>
            </Row>
            <Row height="auto" alignItems="baseline">
              <Col2>
                <OrganisationBankAccountSelector
                  style={{ minWidth: '100%' }}
                  value={donationState.bank_account_id || undefined}
                  onChange={(id:number) => onFieldChange(id, 'bank_account_id')}
                  form={FORM.admin_donation}
                  name="bank_account_id"
                  disabled={processing || isOnlinePayment()} />
              </Col2>
              <Col2>
                {DisplayGatewaySelector()}
              </Col2>
            </Row>
            { isOnlinePayment() && donationState.contact_id && (
            <Row height="auto" style={{ marginTop: '-16px' }}>
              <Col2>
                <PaymentAccountSelector
                  value={donationState.payment_info_id || undefined}
                  type={donationState.payment_type}
                  onChange={(id: number) => onFieldChange(id, 'payment_info_id')}
                  form={FORM.admin_donation}
                  name="payment_info_id"
                  gatewayId={donationState.payment_gateway_id || 0}
                  contactId={donationState.contact_id as number ?? 1} />
              </Col2>
              <Col2>
                { DisplayPaymentInfoSelector() }
              </Col2>
            </Row>
            )}
            <Row height="auto">
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormCheckbox
                  name="is_installment"
                  label={t(lang, 'forms.payments.is_installment')}
                  checked={!!donationState.is_installment}
                  onChange={onFieldChange}
                  disabled={processing} />
              </FormControl>
            </Row>
            <Row height="auto">
              <Col2>
                {DisplayPaymentStatusSelector()}
              </Col2>
              <Col2>
                { DisplayDate() }
              </Col2>
            </Row>
            {!!donationState.is_installment && (
            <>
              <Row height="auto">
                <Col2>
                  <FormTextField
                    form={FORM.admin_donation}
                    label={t(lang, 'forms.payments.number')}
                    name="number"
                    onChange={onInstallmentChange}
                    required
                    disabled={processing}
                    value={installmentState.number || ''} />
                </Col2>
                <Col2>
                  <FormControl style={{ width: '100%', minWidth: 120 }}>
                    <InputLabel id="frequency">Frequency</InputLabel>
                    <Select
                      labelId="frequency-label-id-"
                      id="demo-simple-select"
                      value={installmentState.frequency || ''}
                      onChange={(e) => onInstallmentChange(e.target.value as string, 'frequency')}
                      disabled={processing}>
                      {renderInstallmentFrequencyOptions()}
                    </Select>
                  </FormControl>
                </Col2>
              </Row>
              <Row style={{ marginBottom: '8px' }}>
                {renderInstallmentDetails()}
              </Row>
            </>
            )}
          </>
          )}
          <Subtitle>Meta Data</Subtitle>
          <Row height="auto">
            <Col2>
              <MetadataSelector
                type={MetadataType.program}
                onChange={(id) => onFieldChange(id, 'program_id')}
                form={FORM.admin_donation}
                name="program_id"
                value={donationState.program_id || undefined}
                noMarginTop
                disabled={processing} />
            </Col2>
            <Col2>
              <MetadataSelector
                type={MetadataType.account}
                onChange={(id) => onFieldChange(id, 'account_id')}
                form={FORM.admin_donation}
                name="account_id"
                value={donationState.account_id || undefined}
                noMarginTop
                disabled={processing} />
            </Col2>
          </Row>
          <Row height="auto">
            <Col2>
              <MetadataSelector
                type={MetadataType.department}
                onChange={(id) => onFieldChange(id, 'department_id')}
                form={FORM.admin_donation}
                name="department_id"
                value={donationState.department_id || undefined}
                noMarginTop
                disabled={processing} />
            </Col2>
          </Row>
          <Row height="auto">
            <FormTextField
              form={FORM.admin_donation}
              label="Notes"
              multiline
              minRows="4"
              name="note"
              onChange={onFieldChange}
              value={donationState.note || ''}
              disabled={processing} />
          </Row>
        </Form>
      </Scroller>
    </>
  );
};

export default MakeDonationForm;
